/**
 * sanitizeAiText — Defense-in-depth stripper for AI-generated prose.
 *
 * The AI is instructed (see system prompt + memory rule feedback_no_raw_ids_in_ui)
 * to never emit raw UUIDs, [id:xxx] brackets, or [O1]/[E2] marker tokens
 * in user-facing text. This helper is the last line of defense: even if
 * the model leaks an ID into a SWOT bullet or evaluation reasoning, the
 * user shouldn't see it.
 *
 * Safe to apply to any AI-generated string before rendering. Returns the
 * input unchanged if no markers are present.
 *
 * Strips:
 *   [id:abcdef12]                           → ""
 *   [id:abcdef12, id:xyz45678]              → ""
 *   (id:abcdef12)                           → ""
 *   [O1], [E2], [O12] (opaque ref tokens)   → ""
 *
 * Does NOT touch legitimate bracket content like [1], [2] research
 * citations, [lähde: ...], or plain parenthetical prose.
 */

const ID_BRACKET_RE =
  /\s*\[\s*(?:id:\s*[a-f0-9-]{4,40}(?:\s*,\s*(?:id:\s*)?[a-f0-9-]{4,40})*)\s*\]/gi;
const ID_PAREN_RE =
  /\s*\(\s*id:\s*[a-f0-9-]{4,40}(?:\s*,\s*(?:id:\s*)?[a-f0-9-]{4,40})*\s*\)/gi;
const OPAQUE_TOKEN_RE = /\s*\[[OEoe][0-9]{1,3}\]/g;

export function sanitizeAiText(text) {
  if (!text || typeof text !== 'string') return text;
  if (!text.includes('[') && !text.includes('(id:')) return text;
  return text
    .replace(ID_BRACKET_RE, '')
    .replace(ID_PAREN_RE, '')
    .replace(OPAQUE_TOKEN_RE, '')
    // Collapse any double spaces left by the strips
    .replace(/ {2,}/g, ' ')
    .trim();
}

/**
 * Recursively sanitize all string values inside a JSON-like structure.
 * Useful for passing whole ai_analysis blobs through before rendering.
 */
export function sanitizeAiObject(value) {
  if (typeof value === 'string') return sanitizeAiText(value);
  if (Array.isArray(value)) return value.map(sanitizeAiObject);
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = sanitizeAiObject(v);
    return out;
  }
  return value;
}

export default sanitizeAiText;
